/**
 * Module 'teams': orgs and workspaces, teams of members, project owners and an on-duty
 * rota, approval routing and escalation, mention-style assignment of Needs-you items,
 * presence, and per-member notification preferences (architecture doc sections 4, 7.1
 * and 8.3, open question 2; ADR 0007).
 *
 * People are named by login everywhere (the GitHub login, or 'owner' for the owner link),
 * because that is what the audit log and the actor carry, and what a person types after
 * an @. A team is named by its handle: '@platform'.
 *
 * The routing functions are pure and shared, so the hub and the browser explain the same
 * decision the same way.
 */
import type { Id, Ts } from '../index.ts';
import type { ApprovalKind, Risk } from './approvals.ts';
import type { Role } from './auth.ts';
import type { Urgency } from './notify.ts';

// ---------------------------------------------------------------------------
// Orgs and workspaces
// ---------------------------------------------------------------------------

export interface Org {
  id: Id;
  name: string;
  createdAt: Ts;
}

/**
 * A named slice of an org's projects. The built-in 'All projects' workspace (projectIds
 * null) always exists; the switcher in the top bar scopes the whole console to one.
 */
export interface Workspace {
  id: Id;
  orgId: Id;
  name: string;
  /** null: every project, including ones added later. */
  projectIds: Id[] | null;
  builtIn: boolean;
  createdAt: Ts;
  createdBy: string | null;
}

/** The workspace every org starts with. */
export const ALL_PROJECTS_WORKSPACE_ID = 'wsp_all';

// ---------------------------------------------------------------------------
// Teams, owners, rota
// ---------------------------------------------------------------------------

export interface Team {
  id: Id;
  orgId: Id;
  name: string;
  /** Lowercase, no @: 'platform'. Mentioned as @platform. */
  handle: string;
  /** Member logins. */
  members: string[];
  createdAt: Ts;
  createdBy: string;
}

/** Someone covering a slice of time, ahead of the regular rotation. */
export interface RotaOverride {
  login: string;
  from: Ts;
  to: Ts;
  note?: string;
}

/**
 * Who is on duty for a project. Members take turns of `shiftHours`, counted from
 * `startsAt`; an override covering now wins.
 */
export interface OwnershipRota {
  members: string[];
  shiftHours: number;
  startsAt: Ts;
  overrides: RotaOverride[];
}

export interface ProjectOwnership {
  projectId: Id;
  /** Logins and @team handles. */
  owners: string[];
  rota: OwnershipRota | null;
  updatedAt: Ts;
  updatedBy: string;
}

// ---------------------------------------------------------------------------
// Routing
// ---------------------------------------------------------------------------

/** Where a matching request goes: the project's owners, whoever is on duty, the session owner, or a named person or @team. */
export type RouteTarget = 'owners' | 'on-duty' | 'session-owner' | string;

export interface TeamsRoutingRule {
  id: Id;
  name: string;
  /** Every condition given must hold; an empty `when` matches everything. */
  when: { prod?: boolean; risk?: Risk[]; kinds?: ApprovalKind[]; projectIds?: Id[] };
  to: RouteTarget;
  enabled: boolean;
}

export interface RoutingSettings {
  /** In order; the first rule that matches and names somebody wins. */
  rules: TeamsRoutingRule[];
  /** A request is prod-touching when its command, path or title contains one of these as whole words (case-insensitive; simple /regex/ allowed, see prodPatternProblem). */
  prodPatterns: string[];
  /** Hand an unacknowledged item to the next person on call after this long; null: never. */
  escalateAfterMinutes: number | null;
}

export const DEFAULT_PROD_PATTERNS: readonly string[] = [
  'prod',
  'production',
  'deploy',
  'release',
  'git push origin main',
  'git push origin master',
  'terraform apply',
  'kubectl',
  'wrangler deploy',
  '.env.production',
];

export const DEFAULT_ROUTING_RULES: readonly TeamsRoutingRule[] = [
  { id: 'rte_prod', name: 'Production goes to the project owners', when: { prod: true }, to: 'owners', enabled: true },
  { id: 'rte_duty', name: 'Everything else goes to whoever is on duty', when: {}, to: 'on-duty', enabled: true },
];

export function defaultRoutingSettings(): RoutingSettings {
  return {
    rules: DEFAULT_ROUTING_RULES.map((r) => ({ ...r, when: { ...r.when } })),
    prodPatterns: [...DEFAULT_PROD_PATTERNS],
    escalateAfterMinutes: 10,
  };
}

// ---------------------------------------------------------------------------
// Assignments (Needs-you items handed to people)
// ---------------------------------------------------------------------------

/**
 * What an assignment points at. Keys follow the Needs-you list: s:<sessionId>,
 * a:<approvalId>, m:<machineId>, w:<checkoutId>, p:<pipelineKey>:<itemId>, msn:<missionId>.
 */
export type AssignmentRefType = 'session' | 'approval' | 'machine' | 'worktree' | 'pipeline' | 'mission';

/** mention: a person typed @someone. routed: the routing rules picked. escalated: nobody answered in time. claimed: someone took it. */
export type AssignmentReason = 'mention' | 'routed' | 'escalated' | 'claimed';

export interface Assignment {
  /** The Needs-you key. One assignment per key. */
  key: string;
  refType: AssignmentRefType;
  title: string;
  projectId: Id | null;
  /** Logins (teams are expanded when assigned, so escalation and presence are per person). */
  assignees: string[];
  /** Who assigned it: a login, or 'routing'. */
  by: string;
  at: Ts;
  reason: AssignmentReason;
  /** 0 when first routed; each escalation adds one. */
  level: number;
  ruleId?: string | null;
  /** Why these people, in a sentence ("On duty for dev-portal"). */
  why: string;
  note?: string;
  /** An assignee said they are on it: escalation stops. */
  ackedBy?: string;
  ackedAt?: Ts;
  /** After this the item no longer needs anyone (an approval's own timeout). */
  expiresAt?: Ts;
}

// ---------------------------------------------------------------------------
// Presence
// ---------------------------------------------------------------------------

/** Who else is looking at a view: 'session:<id>', 'flow:<runId>', 'mission:<id>', 'pipeline:<key>'. */
export interface PresenceViewer {
  login: string;
  name: string;
  /** When they opened it. */
  since: Ts;
  /** Their last heartbeat. */
  at: Ts;
  /** How many of their tabs show it. */
  tabs: number;
}

/** Browsers beat this often while a view is open and the tab is visible. */
export const PRESENCE_BEAT_MS = 15_000;
/** A viewer with no beat for this long has left. */
export const PRESENCE_TTL_MS = 45_000;

// ---------------------------------------------------------------------------
// Per-member notification preferences
// ---------------------------------------------------------------------------

export type TeamsNotifyEvent = 'assigned' | 'mentioned' | 'routed' | 'escalated' | 'on-duty';
export const TEAMS_NOTIFY_EVENTS: readonly TeamsNotifyEvent[] = ['assigned', 'mentioned', 'routed', 'escalated', 'on-duty'];

export const TEAMS_NOTIFY_EVENT_LABEL: Record<TeamsNotifyEvent, string> = {
  assigned: 'Someone assigns an item to me',
  mentioned: 'Someone mentions me or my team',
  routed: 'Routing sends a request to me',
  escalated: 'A request escalates to me',
  'on-duty': 'My on-duty shift starts',
};

export interface MemberPrefs {
  login: string;
  events: Record<TeamsNotifyEvent, boolean>;
  /** Below this, nothing is sent to me (it still shows in the app). */
  minUrgency: Urgency;
  /** Local 'HH:MM' window when only critical items reach me now; the rest wait for the digest. */
  quietHours: { from: string; to: string } | null;
  updatedAt: Ts;
}

export function defaultMemberPrefs(login: string): MemberPrefs {
  return {
    login,
    events: { assigned: true, mentioned: true, routed: true, escalated: true, 'on-duty': false },
    minUrgency: 'normal',
    quietHours: null,
    updatedAt: 0,
  };
}

// ---------------------------------------------------------------------------
// State and API shapes
// ---------------------------------------------------------------------------

/** A signed-in person as teammates see them: no tokens, no sign-in details. */
export interface Person {
  login: string;
  name: string;
  role: Role;
}

/**
 * state.ext.teams (SSE 'ext' teams 'state' replaces it). Every signed-in browser gets it, so
 * it carries names and logins of members but no tokens, prefs or sign-in details.
 */
export interface TeamsState {
  rev: number;
  orgs: Org[];
  workspaces: Workspace[];
  teams: Team[];
  ownership: ProjectOwnership[];
  routing: RoutingSettings;
  /** Open assignments, newest first. */
  assignments: Assignment[];
  /** View key to the people looking at it now. */
  presence: Record<string, PresenceViewer[]>;
  people: Person[];
  /** Session id to its owner's login: who launched it from Orgus, or who claimed it. */
  sessionOwners: Record<Id, string>;
}

/** POST /api/teams/assign: mention-style assignment of a Needs-you item. */
export interface AssignRequest {
  key: string;
  title: string;
  projectId?: Id | null;
  /** '@alice @platform please look', or just ['alice', '@platform']. */
  to: string | string[];
  note?: string;
}

/** POST /api/teams/route: what routing would do with a request (also used by the approvals module). */
export interface RouteInput {
  projectId: Id | null;
  sessionOwner?: string | null;
  missionOwner?: string | null;
  kind?: ApprovalKind;
  risk?: Risk;
  /** The command, path or title routing looks at for prod patterns. */
  subject?: string;
}

export interface RouteDecision {
  assignees: string[];
  /** The rule that decided, or null for the default (session owner, or nobody). */
  ruleId: string | null;
  target: RouteTarget | 'nobody';
  prod: boolean;
  why: string;
}

/** Audit actions this module writes (machine 'hub'). */
export const TEAMS_AUDIT_LABEL: Record<string, string> = {
  'teams.org.rename': 'Rename organisation',
  'teams.workspace.create': 'Create workspace',
  'teams.workspace.update': 'Change workspace',
  'teams.workspace.delete': 'Delete workspace',
  'teams.team.create': 'Create team',
  'teams.team.update': 'Change team',
  'teams.team.delete': 'Delete team',
  'teams.ownership.set': 'Set project owners and rota',
  'teams.routing.set': 'Change approval routing',
  'teams.assign': 'Assign',
  'teams.unassign': 'Clear assignment',
  'teams.ack': 'Acknowledge',
  'teams.escalate': 'Escalate',
  'teams.claim': 'Claim',
};

// ---------------------------------------------------------------------------
// Pure functions shared by the hub and the browser
// ---------------------------------------------------------------------------

const HANDLE = /^[a-z0-9][a-z0-9._-]{0,38}$/i;

/** True for a login or a team handle (without the @). */
export function isHandle(v: unknown): v is string {
  return typeof v === 'string' && HANDLE.test(v);
}

/** Every @handle in a piece of text, lowercase, in order, without repeats. */
export function parseMentions(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(/(?:^|[^\w@.])@([a-z0-9][a-z0-9._-]{0,38})/gi)) {
    const h = m[1]!.replace(/[.]+$/, '').toLowerCase();
    if (h && !out.includes(h)) out.push(h);
  }
  return out;
}

/** Logins behind a list of logins and @team handles: teams expand to their members. Unknown handles are kept as logins. */
export function expandTargets(targets: readonly string[], teams: readonly Pick<Team, 'handle' | 'members'>[]): string[] {
  const out: string[] = [];
  const add = (l: string) => {
    const k = l.toLowerCase();
    if (!out.includes(k)) out.push(k);
  };
  for (const raw of targets) {
    const t = raw.trim().replace(/^@/, '').toLowerCase();
    if (!t) continue;
    const team = teams.find((x) => x.handle.toLowerCase() === t);
    if (team) team.members.forEach(add);
    else add(t);
  }
  return out;
}

/** The rota's regular turn at `now`, ignoring overrides; null when it has nobody. */
function turnIndex(rota: OwnershipRota, now: Ts): number | null {
  const n = rota.members.length;
  if (!n) return null;
  const shift = Math.max(1, rota.shiftHours) * 3_600_000;
  const k = Math.floor((now - rota.startsAt) / shift);
  return ((k % n) + n) % n;
}

/** Who is on duty at `now`: an override covering now, else the regular turn. */
export function onDuty(rota: OwnershipRota | null | undefined, now: Ts): string | null {
  if (!rota) return null;
  const o = rota.overrides.find((x) => x.from <= now && now < x.to);
  if (o) return o.login;
  const i = turnIndex(rota, now);
  return i === null ? null : rota.members[i]!;
}

/** When the current regular shift ends (overrides aside). */
export function shiftEndsAt(rota: OwnershipRota, now: Ts): Ts {
  const shift = Math.max(1, rota.shiftHours) * 3_600_000;
  return rota.startsAt + (Math.floor((now - rota.startsAt) / shift) + 1) * shift;
}

/** On-call order from now: whoever is on duty, then the rest of the rotation in turn. */
export function onCallOrder(rota: OwnershipRota | null | undefined, now: Ts): string[] {
  if (!rota) return [];
  const out: string[] = [];
  const first = onDuty(rota, now);
  if (first) out.push(first);
  const i = turnIndex(rota, now);
  if (i !== null) {
    for (let k = 0; k < rota.members.length; k++) {
      const m = rota.members[(i + k) % rota.members.length]!;
      if (!out.includes(m)) out.push(m);
    }
  }
  return out;
}

/** Prod detection reads at most this much of a request's command, path or title. */
export const PROD_SUBJECT_MAX = 2000;
/** A /regex/ pattern reads at most this much (the title, or the start of a command). */
export const PROD_REGEX_SUBJECT_MAX = 500;
/** Limits that keep a /regex/ pattern from backtracking for long (ReDoS). */
const REGEX_MAX_REPEATS = 1;
/** Ways a pattern's optional parts (?) and grouped alternatives (a|b) can combine. */
const REGEX_MAX_BRANCHES = 4;
const REGEX_MAX_TOP_ALTERNATIVES = 10;
/** A pattern that ever takes longer than this to run is switched off for the life of the process. */
const REGEX_SLOW_MS = 50;

const SLASHED = /^\/(.+)\/([a-z]*)$/;

/**
 * Why a production pattern cannot be used, in a sentence, or null when it is fine. Plain
 * words are always fine. A /regular expression/ must compile and stay simple enough that it
 * can never stall the hub: at most one repeat (*, + or {n,m}), no repeat or | inside a
 * repeated group, no back-references, few optional parts and grouped alternatives, and
 * only the flags i, m, s and u.
 */
export function prodPatternProblem(p: string): string | null {
  const m = SLASHED.exec(p.trim());
  if (!m) return null;
  const src = m[1]!;
  try {
    new RegExp(src, m[2]);
  } catch {
    return 'It is not a valid regular expression.';
  }
  if (/[^imsu]/.test(m[2]!)) return 'Only the flags i, m, s and u are allowed.';
  let repeats = 0;
  let branches = 1;
  let topAlts = 0;
  const groups: { quant: boolean; alts: number }[] = [{ quant: false, alts: 0 }];
  const mark = () => groups.forEach((g) => (g.quant = true));
  /** A quantifier at src[i]: how long it is and whether it repeats (vs. optional), or null. */
  const quantAt = (i: number): { len: number; repeat: boolean } | null => {
    const c = src[i];
    let len = 0;
    let repeat = false;
    if (c === '*' || c === '+') [len, repeat] = [1, true];
    else if (c === '?') [len, repeat] = [1, false];
    else if (c === '{') {
      const b = /^\{(\d+)(,(\d*))?\}/.exec(src.slice(i));
      if (!b) return null;
      const max = b[2] === undefined ? Number(b[1]) : b[3] === '' ? Infinity : Number(b[3]);
      [len, repeat] = [b[0].length, max > 1];
    } else return null;
    if (src[i + len] === '?') len++; // lazy
    return { len, repeat };
  };
  const count = (q: { repeat: boolean }) => {
    if (q.repeat) repeats++;
    else branches *= 2;
    mark();
  };
  for (let i = 0; i < src.length; i++) {
    const c = src[i]!;
    if (c === '\\') {
      if (/[1-9k]/.test(src[i + 1] ?? '')) return 'Back-references (\\1, \\k<name>) are not allowed.';
      i++;
      continue;
    }
    if (c === '[') {
      for (i++; i < src.length && src[i] !== ']'; i++) if (src[i] === '\\') i++;
      continue;
    }
    if (c === '(') {
      groups.push({ quant: false, alts: 0 });
      const pre = /^\(\?(?:[:=!]|<[=!]|<[A-Za-z_$][\w$]*>)/.exec(src.slice(i));
      if (pre) i += pre[0].length - 1;
      continue;
    }
    if (c === '|') {
      if (groups.length === 1) topAlts++;
      else groups[groups.length - 1]!.alts++;
      continue;
    }
    if (c === ')') {
      if (groups.length === 1) continue;
      const g = groups.pop()!;
      const parent = groups[groups.length - 1]!;
      branches *= g.alts + 1;
      // A group holding alternatives counts as alternatives in the group around it.
      if (g.alts && groups.length > 1) parent.alts = Math.max(parent.alts, 1);
      const q = quantAt(i + 1);
      if (q) {
        if (q.repeat && (g.quant || g.alts)) return 'A repeated group cannot hold another repeat or a |, as in (a+)+ or (a|b)*.';
        count(q);
        i += q.len;
      }
      if (g.quant) mark();
      continue;
    }
    const q = quantAt(i);
    if (q) {
      count(q);
      i += q.len - 1;
    }
  }
  if (repeats > REGEX_MAX_REPEATS) return `It repeats ${repeats} things; a pattern may repeat one (one *, + or {n,m}).`;
  if (branches > REGEX_MAX_BRANCHES) return 'It has too many optional parts (?) or grouped alternatives (a|b); split it into several patterns.';
  if (topAlts >= REGEX_MAX_TOP_ALTERNATIVES) return `It has ${topAlts + 1} alternatives; split it into several patterns.`;
  return null;
}

/** Compiled /regex/ patterns (null: unusable), so a pattern is checked and compiled once. */
const compiled = new Map<string, RegExp | null>();
/** Patterns that once ran slowly: skipped from then on. */
const slow = new Set<string>();

function compileProd(p: string): RegExp | null {
  let re = compiled.get(p);
  if (re !== undefined) return re;
  const m = SLASHED.exec(p.trim());
  re = null;
  if (m && !prodPatternProblem(p)) {
    try {
      re = new RegExp(m[1]!, m[2]!.includes('i') ? m[2] : `${m[2]}i`);
    } catch {
      re = null;
    }
  }
  if (compiled.size > 500) compiled.clear();
  compiled.set(p, re);
  return re;
}

const isWordChar = (c: string | undefined) => !!c && /[a-z0-9]/.test(c);

/** `needle` (lowercase) appears in `hay` (lowercase) as a whole word at its alphanumeric ends. */
function containsWord(hay: string, needle: string): boolean {
  const atStart = isWordChar(needle[0]);
  const atEnd = isWordChar(needle[needle.length - 1]);
  for (let i = hay.indexOf(needle); i >= 0; i = hay.indexOf(needle, i + 1)) {
    if (atStart && isWordChar(hay[i - 1])) continue;
    if (atEnd && isWordChar(hay[i + needle.length])) continue;
    return true;
  }
  return false;
}

/**
 * True when `text` matches one of the prod patterns. Plain words match case-insensitively as
 * whole words ('prod' matches 'prod-db' but not 'product'; 'deploy' does not match
 * 'deployment'). /regex/flags patterns run on the first 500 characters; a pattern
 * prodPatternProblem refuses never matches.
 */
export function isProdTouching(text: string | undefined | null, patterns: readonly string[]): boolean {
  if (!text) return false;
  const subject = text.slice(0, PROD_SUBJECT_MAX);
  const lower = subject.toLowerCase();
  for (const p of patterns) {
    if (!p) continue;
    if (SLASHED.test(p.trim())) {
      if (slow.has(p)) continue;
      const re = compileProd(p);
      if (!re) continue;
      const t = Date.now();
      const hit = re.test(subject.slice(0, PROD_REGEX_SUBJECT_MAX));
      if (Date.now() - t > REGEX_SLOW_MS) slow.add(p);
      if (hit) return true;
    } else if (containsWord(lower, p.toLowerCase())) return true;
  }
  return false;
}

export interface RoutingContext {
  ownership: readonly ProjectOwnership[];
  teams: readonly Pick<Team, 'handle' | 'members'>[];
  routing: RoutingSettings;
  now: Ts;
}

function ruleMatches(rule: TeamsRoutingRule, input: RouteInput, prod: boolean): boolean {
  const w = rule.when;
  if (w.prod !== undefined && w.prod !== prod) return false;
  if (w.risk?.length && (!input.risk || !w.risk.includes(input.risk))) return false;
  if (w.kinds?.length && (!input.kind || !w.kinds.includes(input.kind))) return false;
  if (w.projectIds?.length && (!input.projectId || !w.projectIds.includes(input.projectId))) return false;
  return true;
}

/** Who a target names for this request, and why, in words. */
export function resolveTarget(target: RouteTarget, input: RouteInput, ctx: RoutingContext): { assignees: string[]; why: string } {
  const own = input.projectId ? ctx.ownership.find((o) => o.projectId === input.projectId) : undefined;
  if (target === 'owners') {
    const a = expandTargets(own?.owners ?? [], ctx.teams);
    return { assignees: a, why: a.length ? 'Owns this project' : 'This project has no owners' };
  }
  if (target === 'on-duty') {
    const d = onDuty(own?.rota, ctx.now);
    return { assignees: d ? [d] : [], why: d ? 'On duty for this project' : 'Nobody is on duty for this project' };
  }
  if (target === 'session-owner') {
    const o = input.missionOwner || input.sessionOwner;
    return { assignees: o ? [o.toLowerCase()] : [], why: input.missionOwner ? 'Owns the mission' : o ? 'Owns the session' : 'The session has no owner' };
  }
  const a = expandTargets([target], ctx.teams);
  return { assignees: a, why: `Named by the rule (${target.startsWith('@') ? target : `@${target}`})` };
}

/**
 * Who should answer a request. Rules run in order; a rule that matches but names nobody
 * (no owners, no rota) passes to the next. When none decides, the session owner (or the
 * mission owner) gets it; with no owner at all, nobody is picked and everyone sees it.
 */
export function routeRequest(input: RouteInput, ctx: RoutingContext): RouteDecision {
  const prod = isProdTouching(input.subject, ctx.routing.prodPatterns);
  for (const rule of ctx.routing.rules) {
    if (!rule.enabled || !ruleMatches(rule, input, prod)) continue;
    const r = resolveTarget(rule.to, input, ctx);
    if (r.assignees.length) return { assignees: r.assignees, ruleId: rule.id, target: rule.to, prod, why: `${r.why}. Rule: ${rule.name}.` };
  }
  const fallback = resolveTarget('session-owner', input, ctx);
  if (fallback.assignees.length) return { assignees: fallback.assignees, ruleId: null, target: 'session-owner', prod, why: `${fallback.why} (the default).` };
  return { assignees: [], ruleId: null, target: 'nobody', prod, why: 'No owner, rota or session owner is set, so it stays with everyone.' };
}

/**
 * Who is next when an item escalates: the project's on-call order, then its owners, skipping
 * anyone already holding it. Null when there is nobody left.
 */
export function nextOnCall(projectId: Id | null, current: readonly string[], ctx: RoutingContext): string | null {
  const own = projectId ? ctx.ownership.find((o) => o.projectId === projectId) : undefined;
  const chain = [...onCallOrder(own?.rota, ctx.now), ...expandTargets(own?.owners ?? [], ctx.teams)];
  const held = new Set(current.map((c) => c.toLowerCase()));
  return chain.find((l) => !held.has(l.toLowerCase())) ?? null;
}

/** Should an escalation fire now? */
export function escalationDue(a: Pick<Assignment, 'reason' | 'at' | 'ackedAt'>, escalateAfterMinutes: number | null, now: Ts): boolean {
  if (escalateAfterMinutes == null || escalateAfterMinutes <= 0) return false;
  if (a.ackedAt || a.reason === 'claimed') return false;
  return now - a.at >= escalateAfterMinutes * 60_000;
}

function hm(s: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h < 24 && min < 60 ? h * 60 + min : null;
}

/** Inside a local 'HH:MM' to 'HH:MM' window (which may cross midnight)? */
export function inWindow(q: { from: string; to: string } | null | undefined, minutes: number): boolean {
  if (!q) return false;
  const a = hm(q.from);
  const b = hm(q.to);
  if (a === null || b === null || a === b) return false;
  return a < b ? minutes >= a && minutes < b : minutes >= a || minutes < b;
}

const URGENCY_ORDER: readonly Urgency[] = ['low', 'normal', 'high', 'critical'];

/**
 * One member's preference for one notification: send now, hold for the digest (quiet
 * hours), or skip (event off, or below their floor). Critical always gets through.
 */
export function memberWants(prefs: MemberPrefs, event: TeamsNotifyEvent, urgency: Urgency, localMinutes: number): 'send' | 'digest' | 'skip' {
  if (urgency !== 'critical' && !prefs.events[event]) return 'skip';
  if (urgency === 'critical') return 'send';
  if (URGENCY_ORDER.indexOf(urgency) < URGENCY_ORDER.indexOf(prefs.minUrgency)) return 'skip';
  if (inWindow(prefs.quietHours, localMinutes)) return 'digest';
  return 'send';
}

const KEY_PREFIX: Record<string, AssignmentRefType> = { s: 'session', a: 'approval', m: 'machine', w: 'worktree', p: 'pipeline', msn: 'mission' };

/** What a Needs-you key points at, or null when it is not one. */
export function refTypeOfKey(key: string): AssignmentRefType | null {
  const i = key.indexOf(':');
  if (i <= 0 || i === key.length - 1 || key.length > 400) return null;
  return KEY_PREFIX[key.slice(0, i)] ?? null;
}

/** Where a Needs-you key opens in the console. */
export function assignmentUrl(key: string): string {
  const rest = key.slice(key.indexOf(':') + 1);
  const e = encodeURIComponent;
  switch (refTypeOfKey(key)) {
    case 'session':
      return `/sessions?session=${e(rest)}`;
    case 'approval':
      return `/inbox?approval=${e(rest)}`;
    case 'machine':
      return `/machines?machine=${e(rest)}`;
    case 'worktree':
      return `/projects?checkout=${e(rest)}`;
    case 'pipeline': {
      const j = rest.lastIndexOf(':');
      return j > 0 ? `/pipelines?pipeline=${e(rest.slice(0, j))}&item=${e(rest.slice(j + 1))}` : '/pipelines';
    }
    case 'mission':
      return `/missions?mission=${e(rest)}`;
    default:
      return '/';
  }
}

/** The project ids a workspace shows; null for every project. */
export function workspaceProjects(ws: Pick<Workspace, 'projectIds'> | null | undefined): Id[] | null {
  return ws?.projectIds ?? null;
}
